/**
 * The "Edit with AI" panel (docs/plans/lms-course-editor-plan.md 8.2, 8.5,
 * 8.6, 8.8, 4.10, R1, R4 to R6, R12, R14; WP-E30), mounted by VersionEditor
 * (WP-E31) with 8.2's props:
 *
 *   { versionId, courseId, sections: { id, title }[], isDraft, locked,
 *     onApplied(), onBeforeSend(): Promise<boolean> }
 *
 * - Top: the one-line explanation and the counts ("12 of 30 messages used
 *   on this draft"). Middle: the messages, oldest first, scrolled to the
 *   newest (EditMessageCard). Bottom: the composer (EditComposer), disabled
 *   while the draft is locked, while a message is still working, or while a
 *   message waits at "Ready" for its Send.
 * - The newest working message is polled every 5 s while the page is
 *   visible (useEditMessages); when it becomes applied the panel calls
 *   onApplied() so the editor reloads the draft in place (8.5). A revert also
 *   calls onApplied(), because the draft changed on the server and the editor
 *   must not keep (or autosave) the reverted text.
 * - On a published version (isDraft false) the panel is the version's
 *   history, read-only: "Edited with AI: n messages", every message with its
 *   diff, no composer and no action but View changes (8.6); no estimate is
 *   asked (the route answers 409 on a published version).
 * - A card-style collapsible (the SourcesPanel's pattern): VersionEditor
 *   places it beside the editor on wide screens and above it on narrow ones.
 *
 * One optional prop beyond 8.2, selectedSectionId: the section chosen in the
 * content editor, ticked first when the admin picks "Only these sections".
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, MessageSquareText, Sparkles } from 'lucide-react';
import EmptyState from '../../../../../ui/EmptyState';
import type { EditEstimate, EditMessage, EditMessageAndRun } from '../../../../api/editor';
import { noticeCls } from '../../../../components/player/styles';
import { cardCls, hintCls } from '../styles';
import { EditComposer } from './EditComposer';
import { EditMessageCard } from './EditMessageCard';
import type { EditMessageChange } from './EditMessageCard';
import {
  countsLine,
  EMPTY_HINT,
  EMPTY_TITLE,
  isPolledMessage,
  isReadyMessage,
  limitsOf,
  PANEL_EXPLANATION,
} from './editModel';
import type { EditMessageState, EditSectionRef } from './editModel';
import { useEditMessages } from './useEditMessages';

export interface EditPanelProps {
  versionId: string;
  courseId: string;
  sections: EditSectionRef[];
  isDraft: boolean;
  /** The editor's lock (a run is working on the draft): the composer is disabled. */
  locked: boolean;
  /** A message was applied (or reverted): reload the draft in place. */
  onApplied: () => void;
  /** Flush the editor's autosave before a send; false stops it. */
  onBeforeSend: () => Promise<boolean>;
  /** Optional, beyond 8.2: the section chosen in the content editor. */
  selectedSectionId?: string | null;
  /** Start collapsed (default open). */
  defaultOpen?: boolean;
}

export function EditPanel({
  versionId,
  courseId,
  sections,
  isDraft,
  locked,
  onApplied,
  onBeforeSend,
  selectedSectionId = null,
  defaultOpen = true,
}: EditPanelProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [estimate, setEstimate] = useState<EditEstimate | null>(null);
  const appliedRef = useRef(onApplied);
  appliedRef.current = onApplied;
  const listRef = useRef<HTMLOListElement | null>(null);

  const onSettled = useCallback((message: EditMessage, previous: EditMessageState) => {
    void previous;
    if (message.outcome === 'applied') appliedRef.current();
  }, []);

  const { entries, loading, error, refresh, upsert } = useEditMessages(versionId, { onSettled });
  const count = entries.length;

  // Keep the newest message in view.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count, open]);

  const working = entries.find((e) => isPolledMessage(e.message)) ?? null;
  const ready = entries.find((e) => isReadyMessage(e.message)) ?? null;
  const composerDisabled = locked || Boolean(working) || Boolean(ready);
  const disabledReason = locked
    ? 'The draft is read-only while a message or a course builder run is working on it.'
    : working
      ? `Message ${working.message.ordinal} is still working; the next message can be sent when it finishes.`
      : ready
        ? `Send or discard message ${ready.message.ordinal} first.`
        : null;

  const limits = limitsOf(estimate);
  const used = limits.usedMessages ?? count;

  const onSent = (result: EditMessageAndRun) => {
    if (result && result.message) upsert(result.message, result.run ?? null);
  };

  const onChanged = (change: EditMessageChange) => {
    upsert(change.message, change.run);
    if (change.draftChanged) appliedRef.current();
  };

  const summary = isDraft
    ? `${count} message${count === 1 ? '' : 's'}`
    : `Edited with AI: ${count} message${count === 1 ? '' : 's'}`;

  return (
    <section
      className={`${cardCls} space-y-3`}
      aria-label="Edit with AI"
      data-testid="lms-edit-panel"
      data-version-id={versionId}
      data-course-id={courseId}
    >
      <button
        type="button"
        className="flex min-h-[44px] w-full items-center gap-2 rounded-lg text-left text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100 sm:min-h-0"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="lms-edit-toggle"
      >
        {open ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
        <Sparkles className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
        Edit with AI
        <span className="text-xs font-normal text-gray-500 dark:text-gray-400" data-testid="lms-edit-summary">
          {`(${summary})`}
        </span>
      </button>

      {/* Hidden, not unmounted, when collapsed: a half-written message survives. */}
      <div className="space-y-3" hidden={!open} data-testid="lms-edit-body">
        {isDraft ? (
          <div className="space-y-1">
            <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="lms-edit-explanation">
              {PANEL_EXPLANATION}
            </p>
            <p className={`${hintCls} tabular-nums`} data-testid="lms-edit-counts">
              {countsLine(used, limits.maxMessages)}
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="lms-edit-history">
            {`Edited with AI: ${count} message${count === 1 ? '' : 's'}. This version is published, so these messages are its history and nothing here can be changed.`}
          </p>
        )}

        {error ? (
          <div className={noticeCls('red')} role="alert" data-testid="lms-edit-list-error">
            <p>
              {`${error.message} `}
              <button
                type="button"
                className="font-medium underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500"
                onClick={refresh}
              >
                Try again
              </button>
            </p>
          </div>
        ) : null}

        {loading && count === 0 ? (
          <div className="flex items-center gap-2 py-4 text-sm text-gray-500 dark:text-gray-400" role="status" data-testid="lms-edit-loading">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
            Loading the messages...
          </div>
        ) : null}

        {!loading && !error && count === 0 ? (
          <div data-testid="lms-edit-empty">
            {isDraft ? (
              <EmptyState icon={MessageSquareText} title={EMPTY_TITLE} hint={EMPTY_HINT} variant="card" />
            ) : (
              <p className={hintCls}>No AI edits were made on this version.</p>
            )}
          </div>
        ) : null}

        {count > 0 ? (
          <ol ref={listRef} className="max-h-[36rem] space-y-3 overflow-y-auto pr-1" data-testid="lms-edit-messages" aria-label="Messages">
            {entries.map((e) => (
              <EditMessageCard
                key={e.message.id}
                message={e.message}
                run={e.run}
                sources={e.sources}
                sections={sections}
                readOnly={!isDraft}
                locked={locked}
                onBeforeSend={onBeforeSend}
                onChanged={onChanged}
              />
            ))}
          </ol>
        ) : null}

        {isDraft ? (
          <EditComposer
            versionId={versionId}
            sections={sections}
            disabled={composerDisabled}
            disabledReason={disabledReason}
            selectedSectionId={selectedSectionId}
            messageCount={count}
            onBeforeSend={onBeforeSend}
            onSent={onSent}
            onEstimate={setEstimate}
          />
        ) : null}
      </div>
    </section>
  );
}

export default EditPanel;
